import { useEffect, useRef } from 'react';

// Lightweight scroll-reveal for the new trust/process/about blocks (Fase 8).
// Adds `is-visible` to any descendant carrying `.reveal-up` (and the element
// itself) the first time it enters the viewport. Kept independent from GSAP so
// it has zero ScrollTrigger overhead and respects prefers-reduced-motion via
// the CSS in index.css.
export const useReveal = ({ threshold = 0.18, stagger = 90 } = {}) => {
  const ref = useRef(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;

    const targets = [
      ...(root.classList.contains('reveal-up') ? [root] : []),
      ...root.querySelectorAll('.reveal-up'),
    ];
    if (targets.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = targets.indexOf(entry.target);
          const delay = Math.max(0, index) * stagger;
          window.setTimeout(() => entry.target.classList.add('is-visible'), delay);
          observer.unobserve(entry.target);
        });
      },
      { threshold, rootMargin: '0px 0px -8% 0px' }
    );

    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [threshold, stagger]);

  return ref;
};

export default useReveal;
